(() => {
  'use strict';

  if (window.manualGuideInitialized) return;
  window.manualGuideInitialized = true;
  const instances = new WeakMap();

  function createPageControl(label, page, isCurrent) {
    const item = document.createElement('li');
    const control = document.createElement(isCurrent ? 'span' : 'a');
    control.textContent = label;
    if (isCurrent) {
      control.setAttribute('aria-current', 'page');
    } else {
      control.href = '#';
      control.dataset.manualPage = String(page);
    }
    item.appendChild(control);
    return item;
  }

  function paginateGrid(grid) {
    const pageSize = parseInt(grid.dataset.pageSize, 10);
    const cards = Array.from(grid.children);
    if (!pageSize || cards.length <= pageSize) return;

    const totalPages = Math.ceil(cards.length / pageSize);
    const nav = document.createElement('nav');
    nav.className = 'manual-guide__pagination';
    nav.setAttribute('aria-label', grid.dataset.labelNav || '');
    const list = document.createElement('ul');
    list.setAttribute('role', 'list');
    nav.appendChild(list);
    grid.after(nav);

    function render(page) {
      cards.forEach((card, index) => {
        card.hidden = Math.floor(index / pageSize) + 1 !== page;
      });
      list.replaceChildren();
      if (page > 1) list.appendChild(createPageControl(grid.dataset.labelPrevious || '‹', page - 1, false));
      for (let number = 1; number <= totalPages; number += 1) {
        list.appendChild(createPageControl(String(number), number, number === page));
      }
      if (page < totalPages) list.appendChild(createPageControl(grid.dataset.labelNext || '›', page + 1, false));
    }

    nav.addEventListener('click', (event) => {
      const control = event.target.closest('[data-manual-page]');
      if (!control) return;
      event.preventDefault();
      event.stopPropagation();
      render(parseInt(control.dataset.manualPage, 10));
      grid.scrollIntoView({
        block: 'start',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    });

    render(1);
  }

  function paginateGrids(scope) {
    scope.querySelectorAll('.manual-guide__grid[data-page-size]').forEach(paginateGrid);
  }

  function initialize(root) {
    if (instances.has(root)) return;

    let request;
    const form = root.querySelector('.manual-guide__search');
    const searchInput = form.querySelector('input[name="q"]');
    const homePath = root.dataset.homeUrl
      ? new URL(root.dataset.homeUrl, window.location.origin).pathname
      : window.location.pathname;

    function syncFragment(nextDocument, selector) {
      const current = root.querySelector(selector);
      const next = nextDocument.querySelector(`[data-manual-guide] ${selector}`);
      if (current && next) current.replaceWith(next);
    }

    function updateNavigation(url, nextDocument) {
      syncFragment(nextDocument, '.manual-guide__categories');
      syncFragment(nextDocument, '[data-manual-back]');
      searchInput.value = url.searchParams.get('q') || '';
    }

    async function navigate(url, addHistory) {
      if (request) request.abort();
      request = new AbortController();
      const currentRequest = request;
      const results = root.querySelector('.manual-guide__results');
      results.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url.href, {
          signal: currentRequest.signal,
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Manual page could not be loaded');
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextResults = documentFragment.querySelector('[data-manual-guide] .manual-guide__results');
        if (!nextResults) throw new Error('Manual template is unavailable');
        if (currentRequest.signal.aborted || !root.isConnected) return;

        results.replaceWith(nextResults);
        paginateGrids(nextResults);
        if (addHistory) window.history.pushState(null, '', url.href);
        document.title = documentFragment.title;
        updateNavigation(url, documentFragment);
        const heading = nextResults.querySelector('h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
        nextResults.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
        });
      } catch (error) {
        if (error.name !== 'AbortError') window.location.assign(url.href);
      } finally {
        if (request === currentRequest) {
          root.querySelector('.manual-guide__results').removeAttribute('aria-busy');
        }
      }
    }

    function onClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a');
      if (!link || !root.contains(link) || link.target || link.hasAttribute('download')) return;
      const url = new URL(link.href);
      if (url.origin !== window.location.origin) return;
      if (url.searchParams.get('view') !== 'manuals' && url.pathname !== homePath) return;
      event.preventDefault();
      navigate(url, true);
    }

    function onSubmit(event) {
      if (event.target !== form) return;
      event.preventDefault();
      const url = new URL(form.action);
      url.search = new URLSearchParams(new FormData(form)).toString();
      navigate(url, true);
    }

    function onPopState() {
      navigate(new URL(window.location.href), false);
    }

    paginateGrids(root);
    root.addEventListener('click', onClick);
    root.addEventListener('submit', onSubmit);
    window.addEventListener('popstate', onPopState);
    instances.set(root, () => {
      if (request) request.abort();
      root.removeEventListener('click', onClick);
      root.removeEventListener('submit', onSubmit);
      window.removeEventListener('popstate', onPopState);
      instances.delete(root);
    });
  }

  document.querySelectorAll('[data-manual-guide]').forEach(initialize);
  if (!window.manualGuideEditorEventsBound) {
    window.manualGuideEditorEventsBound = true;
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('[data-manual-guide]').forEach(initialize);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('[data-manual-guide]').forEach((root) => {
        const dispose = instances.get(root);
        if (dispose) dispose();
      });
    });
  }
})();
